import { FC, useCallback, useMemo } from "react";
import { Stack, Typography } from "@mui/material";
import { RegionsTriggerComponent } from "./RegionsTriggerComponent";
import {
  ProviderType,
  useGetProvidersByProviderRegionsQuery,
} from "../../../../../../services/cloudchipr.api";
import { useAppDispatch, useAppSelector } from "../../../../../../store/hooks";
import { setRegion } from "../../../../../../store/account/accountSlice";
import { currentAccountSelectedRegionsSelector } from "../../../../../../store/account/selectors/current-account/currentAccountSelectedRegionsSelector";
import { accountRefreshDataThunk } from "../../../../../../store/account/thunks/accountRefreshDataThunk";
import { DropdownSelect } from "../../../../../common/select/dropdown-select/DropdownSelect";

interface RegionsProps {
  provider: ProviderType;
}

export const Regions: FC<RegionsProps> = ({ provider }) => {
  const dispatch = useAppDispatch();

  const { data } = useGetProvidersByProviderRegionsQuery({
    provider,
  });

  const currentRegions = useAppSelector(currentAccountSelectedRegionsSelector);
  const allRegions = useMemo(() => data?.map(({ key }) => key) ?? [], [data]);

  const regionsSubmitHandler = useCallback(
    (selectedRegions: string[]) => {
      const appliedRegions = currentRegions ?? allRegions;

      if (appliedRegions.join() === selectedRegions.join()) {
        return;
      }

      dispatch(
        setRegion(selectedRegions.length ? selectedRegions : currentRegions),
      );

      dispatch(accountRefreshDataThunk());
    },
    [dispatch, currentRegions, allRegions],
  );

  const options = useMemo(() => {
    return (
      data?.map((region) => {
        return {
          value: region.key,
          rawValue: region,
          label: (
            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Typography variant="body2">{region.name}</Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="right"
              >
                {region.key}
              </Typography>
            </Stack>
          ),
        };
      }) ?? []
    );
  }, [data]);

  if (!allRegions.length) {
    return null;
  }

  return (
    <DropdownSelect
      label="Regions"
      options={options}
      values={currentRegions ?? undefined}
      initialSelectedValues={currentRegions ? undefined : allRegions}
      TriggerComponent={RegionsTriggerComponent}
      submitHandlerOnClose={regionsSubmitHandler}
      listWidth={320}
    />
  );
};
